

import {
  ChevronRight,
  Visibility as Eye,
  VisibilityOff as EyeOff,
  Home,
  ZoomIn
} from '@mui/icons-material';
import Box from '@mui/material/Box';
import Breadcrumbs from '@mui/material/Breadcrumbs';
import FormControl from '@mui/material/FormControl';
import IconButton from '@mui/material/IconButton';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import { alpha, useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  Background,
  BackgroundVariant,
  Controls,
  MarkerType,
  MiniMap,
  Panel,
  ReactFlow,
  useEdgesState,
  useNodesState,
  type Edge,
  type Node,
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';
import { useCallback, useEffect, useMemo, useState } from 'react';


import { TEMPLATE_LAYOUTS } from './constants';
import { DownloadGraphButton } from './controls/DownloadGraphButton';
import { edgeTypes, FloatingConnectionLine } from './edges';
import { nodeTypes } from './nodes';
import { calculatePositions } from './utils/layout-calculator';

import type { EventSummary as Event, GroupBy, NarrativeGraphViewProps, NarrativeSummary, ZoomLevel } from './types';

type ArcSummary = NarrativeSummary & { color?: string };

import { useAddNodeOnDrop } from '@/components/react-flow/hooks/useAddNodeOnDrop';
import { useDeleteMiddleNode } from '@/components/react-flow/hooks/useDeleteMiddleNode';

/**
 * Inner component that contains all the graph logic.
 * This component MUST be rendered inside a ReactFlowProvider.
 */
export function NarrativeGraphViewInner({
  events,
  arcs,
  structureSections,
  externalSelectedSectionId,
  onExternalSectionChange,
  onSectionUpdate,
  onSectionCreate,
  onSectionDelete,
}: NarrativeGraphViewProps) {
  const theme = useTheme();

  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(
    externalSelectedSectionId ? 'section' : 'structure'
  );
  const [internalSectionId, setInternalSectionId] = useState<string | null>(
    externalSelectedSectionId || null
  );
  const [groupBy, setGroupBy] = useState<GroupBy>('none');
  const [showIsolated, setShowIsolated] = useState(true);

  // Sync with external section selection
  useEffect(() => {
    if (externalSelectedSectionId !== undefined) {
      if (externalSelectedSectionId) {
        setInternalSectionId(externalSelectedSectionId);
        setZoomLevel('section');
      } else {
        setInternalSectionId(null);
        setZoomLevel('structure');
      }
    }
  }, [externalSelectedSectionId]);

  const selectedSectionId = internalSectionId;
  const [flowNodes, setNodes, onNodesChange] = useNodesState<Node>([]);
  const [flowEdges, setEdges, onEdgesChange] = useEdgesState<Edge>([]);

  // Sort sections by order
  const sortedSections = useMemo(() => {
    return [...structureSections].sort((a, b) => a.order - b.order);
  }, [structureSections]);

  // Get layout type from template
  const layoutType = useMemo(() => {
    const templateId = sortedSections[0]?.templateId || 'grid';
    return TEMPLATE_LAYOUTS[templateId] || 'grid';
  }, [sortedSections]);

  // Check if using custom template (enables editing)
  const isCustomTemplate = useMemo(() => {
    const templateId = sortedSections[0]?.templateId;
    return templateId === 'custom';
  }, [sortedSections]);

  const selectedSection = sortedSections.find(s => s.id === selectedSectionId);

  // Handle section label update
  const handleSectionLabelChange = useCallback((sectionId: string, newLabel: string) => {
    onSectionUpdate?.(sectionId, { name: newLabel });
  }, [onSectionUpdate]);

  // Add node on drop hook (only enabled in custom mode at structure level)
  const { onConnectStart, onConnectEnd } = useAddNodeOnDrop({
    defaultNodeType: 'section',
    generateId: () => `section-${Date.now()}`,
    createNodeData: () => ({
      label: 'New Section',
      status: 'notWritten' as const,
      eventCount: 0,
      isEditable: true,
    }),
    onNodeCreated: (node) => {
      if (isCustomTemplate && zoomLevel === 'structure') {
        onSectionCreate?.({
          id: node.id,
          name: 'New Section',
          templateId: 'custom',
          order: sortedSections.length,
        });
      }
    },
  });

  // Delete middle node hook (reconnects edges when deleting a section)
  const { onNodesDelete } = useDeleteMiddleNode({
    onNodesDeleted: (deletedNodes: Node[]) => {
      if (isCustomTemplate && zoomLevel === 'structure') {
        deletedNodes.forEach((node: Node) => {
          onSectionDelete?.(node.id);
        });
      }
    },
  });

  // Compute narrative status for event
  const getEventStatus = useCallback((event: Event): string => {
    if (event.isNarrativelyComplete) return 'complete';
    return 'notWritten';
  }, []);

  // Build nodes and edges based on zoom level
  const { computedNodes, computedEdges } = useMemo(() => {
    const newNodes: Node[] = [];
    const newEdges: Edge[] = [];

    if (zoomLevel === 'structure') {
      const positions = calculatePositions(sortedSections, layoutType);
      const layoutSections = sortedSections.filter(s => s.id !== 'unassigned');

      layoutSections.forEach((section) => {
        const sectionEvents = events.filter(e => e.structureSectionId === section.id);
        const pos = positions.get(section.id) || { x: 0, y: 0 };

        // Calculate section status based on events
        const completeCount = sectionEvents.filter(e => e.isNarrativelyComplete).length;
        const totalCount = sectionEvents.length;
        let status: 'complete' | 'partial' | 'notWritten' = 'notWritten';
        if (totalCount > 0) {
          if (completeCount === totalCount) {
            status = 'complete';
          } else if (completeCount > 0) {
            status = 'partial';
          }
        }

        newNodes.push({
          id: section.id,
          type: 'section',
          position: pos,
          data: {
            label: section.name,
            status,
            eventCount: sectionEvents.length,
            isEditable: isCustomTemplate,
            onLabelChange: isCustomTemplate
              ? (newLabel: string) => handleSectionLabelChange(section.id, newLabel)
              : undefined,
          },
        });
      });

      // Connect sequential sections
      for (let i = 0; i < layoutSections.length - 1; i++) {
        const current = layoutSections[i];
        const next = layoutSections[i + 1];



        newEdges.push({
          id: `${current.id}->${next.id}`,
          source: current.id,
          target: next.id,
          type: 'floating',
          style: { stroke: '#94a3b8', strokeWidth: 2 },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#94a3b8',
            width: 12,
            height: 12,
          },
        });
      }

      // For circular layout, connect last to first
      if (layoutType === 'circular' && layoutSections.length > 2) {
        const last = layoutSections[layoutSections.length - 1];
        const first = layoutSections[0];


        newEdges.push({
          id: `${last.id}->${first.id}`,
          source: last.id,
          target: first.id,
          type: 'floating',
          style: { stroke: '#94a3b8', strokeWidth: 2, strokeDasharray: '5 5' },
          markerEnd: {
            type: MarkerType.ArrowClosed,
            color: '#94a3b8',
            width: 12,
            height: 12,
          },
        });
      }

    } else if (zoomLevel === 'section' && selectedSectionId) {
      const sectionEvents = events.filter(e => e.structureSectionId === selectedSectionId);
      const arcIds = [...new Set(sectionEvents.map(e => e.arcId))];
      const spacing = 200;

      arcIds.forEach((arcId, index) => {
        const arc = arcs.find(a => a.id === arcId) as ArcSummary | undefined;
        if (!arc) return;

        const arcEvents = sectionEvents.filter(e => e.arcId === arcId);
        newNodes.push({
          id: `arc-${arcId}`,
          type: 'arcCluster',
          position: { x: 100 + index * spacing, y: 100 },
          data: {
            label: arc.name,
            color: arc.color || '#3b82f6',
            eventCount: arcEvents.length,
          },
        });
      });

    } else if (zoomLevel === 'events') {
      const visibleEvents = selectedSectionId
        ? events.filter(e => e.structureSectionId === selectedSectionId)
        : events;

      const filteredEvents = showIsolated
        ? visibleEvents
        : visibleEvents.filter(e =>
            (e.causesEventIds?.length || 0) > 0 ||
            visibleEvents.some(other => other.causesEventIds?.includes(e.id))
          );

      const cols = 4;
      const colWidth = 220;
      const rowHeight = 100;

      filteredEvents.forEach((event, index) => {
        const section = sortedSections.find(s => s.id === event.structureSectionId);
        const col = index % cols;
        const row = Math.floor(index / cols);

        newNodes.push({
          id: event.id,
          type: 'event',
          position: { x: 50 + col * colWidth, y: 50 + row * rowHeight },
          data: {
            event,
            status: getEventStatus(event),
            color: section?.color,
          },
        });
      });

      // Build causality edges
      filteredEvents.forEach(event => {
        (event.causesEventIds || []).forEach((targetId: string) => {
          if (filteredEvents.some(e => e.id === targetId)) {
            newEdges.push({
              id: `${event.id}->${targetId}`,
              source: event.id,
              target: targetId,
              type: 'floating',
              style: { stroke: '#64748b', strokeWidth: 2 },
              markerEnd: {
                type: MarkerType.ArrowClosed,
                color: '#64748b',
                width: 20,
                height: 20,
              },
            });
          }
        });
      });
    }

    return { computedNodes: newNodes, computedEdges: newEdges };
  }, [zoomLevel, events, sortedSections, arcs, selectedSectionId, showIsolated, getEventStatus, layoutType, isCustomTemplate, handleSectionLabelChange]);

  // Update React Flow state when computed values change
  useEffect(() => {
    setNodes(computedNodes);
    setEdges(computedEdges);
  }, [computedNodes, computedEdges, setNodes, setEdges]);

  // Handle node click for zoom navigation
  const onNodeClick = useCallback((_: React.MouseEvent, node: Node) => {
    if (zoomLevel === 'structure') {
      setInternalSectionId(node.id);
      setZoomLevel('section');
      onExternalSectionChange?.(node.id);
    } else if (zoomLevel === 'section' && node.id.startsWith('arc-')) {
      setZoomLevel('events');
    }
  }, [zoomLevel, onExternalSectionChange]);

  // Breadcrumb navigation
  const handleBreadcrumbClick = (level: ZoomLevel) => {
    setZoomLevel(level);
    if (level === 'structure') {
      setInternalSectionId(null);
      onExternalSectionChange?.(null);
    }
  };

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Controls Bar */}
      <Paper sx={{ p: 1.5, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
        <Breadcrumbs separator={<ChevronRight sx={{ fontSize: 16 }} />}>
          <Link
            component="button"
            underline="hover"
            onClick={() => handleBreadcrumbClick('structure')}
            sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
          >
            <Home sx={{ fontSize: 16, mr: 0.5 }} />
            Narrative Structure
          </Link>
          {selectedSection && (
            <Link
              component="button"
              underline="hover"
              onClick={() => handleBreadcrumbClick('section')}
            >
              {selectedSection.name}
            </Link>
          )}
          {zoomLevel === 'events' && (
            <Typography color="text.primary">Events</Typography>
          )}
        </Breadcrumbs>

        <Box sx={{ flex: 1 }} />

        {/* Graph Controls */}
        {zoomLevel === 'events' && (
          <>
            <FormControl size="small" sx={{ minWidth: 140 }}>
              <InputLabel>Group By</InputLabel>
              <Select
                value={groupBy}
                label="Group By"
                onChange={(e) => setGroupBy(e.target.value as GroupBy)}
              >
                <MenuItem value="none">None</MenuItem>
                <MenuItem value="location">Location</MenuItem>
                <MenuItem value="character">Character</MenuItem>
                <MenuItem value="organization">Organization</MenuItem>
              </Select>
            </FormControl>

            <Tooltip title={showIsolated ? 'Hide isolated nodes' : 'Show isolated nodes'}>
              <IconButton size="small" onClick={() => setShowIsolated(!showIsolated)}>
                {showIsolated ? <Eye sx={{ fontSize: 18 }} /> : <EyeOff sx={{ fontSize: 18 }} />}
              </IconButton>
            </Tooltip>
          </>
        )}
      </Paper>

      {/* Graph Canvas */}
      <Box
        sx={{
          flex: 1,
          bgcolor: 'background.default',
          borderRadius: 2,
          overflow: 'hidden',
          position: 'relative',
        }}
      >
        {structureSections.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
            }}
          >
            <Typography color="text.secondary">
              No structure sections defined. Select a narrative template first.
            </Typography>
          </Box>
        ) : (
          <ReactFlow
            colorMode={theme.palette.mode}
            nodes={flowNodes}
            edges={flowEdges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeClick={onNodeClick}
            onNodesDelete={isCustomTemplate && zoomLevel === 'structure' ? onNodesDelete : undefined}
            onConnectStart={isCustomTemplate && zoomLevel === 'structure' ? onConnectStart : undefined}
            onConnectEnd={isCustomTemplate && zoomLevel === 'structure' ? onConnectEnd : undefined}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            connectionLineComponent={FloatingConnectionLine}
            fitView
            fitViewOptions={{ padding: 0.3, includeHiddenNodes: true }}
            proOptions={{ hideAttribution: true }}
            nodesDraggable={true}
            nodesConnectable={isCustomTemplate && zoomLevel === 'structure'}
            edgesReconnectable={isCustomTemplate && zoomLevel === 'structure'}
            elementsSelectable={true}
            deleteKeyCode={isCustomTemplate && zoomLevel === 'structure' ? ['Backspace', 'Delete'] : null}
            minZoom={0.1}
            maxZoom={2}
            defaultEdgeOptions={{
              type: 'floating',
              style: { stroke: theme.palette.text.secondary, strokeWidth: 2 },
              animated: false,
            }}
          >
            <Background
              variant={BackgroundVariant.Dots}
              gap={24}
              size={1.5}
              color={theme.palette.divider}
            />

            <Controls
              showInteractive={false}
              style={{
                background: theme.palette.background.paper,
                borderRadius: '12px',
                boxShadow: theme.shadows[3],
              }}
            />

            <Panel position="top-left">
              <Box sx={{ display: 'flex', gap: 1 }}>
                <DownloadGraphButton />
              </Box>
            </Panel>

            <MiniMap
              nodeColor={(node) => {
                const data = node.data as { color?: string } | undefined;
                return data?.color || '#94a3b8';
              }}
              nodeStrokeWidth={3}
              nodeBorderRadius={8}
              maskColor="hsl(var(--background) / 0.8)"
              pannable
              zoomable
              style={{
                background: 'hsl(var(--card))',
                borderRadius: '12px',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              }}
            />

            <Panel position="bottom-right">
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  px: 2,
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(8px)',
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <ZoomIn sx={{ fontSize: 14, color: 'text.secondary' }} />
                <Typography variant="caption" color="text.secondary" fontWeight={500}>
                  {zoomLevel === 'structure'
                    ? 'Click a section to explore'
                    : zoomLevel === 'section'
                    ? 'Click an arc to view events'
                    : 'Viewing events'}
                </Typography>
              </Paper>
            </Panel>

            <Panel position="top-right">
              <Paper
                elevation={2}
                sx={{
                  p: 1.5,
                  bgcolor: (theme) => alpha(theme.palette.background.paper, 0.95),
                  backdropFilter: 'blur(8px)',
                  borderRadius: 2,
                }}
              >
                <Typography variant="caption" fontWeight={600} color="text.secondary" display="block" sx={{ mb: 1 }}>
                  Status Legend
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#10b981' }} />
                    <Typography variant="caption">Complete</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#f59e0b' }} />
                    <Typography variant="caption">Partial</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: '#6b7280' }} />
                    <Typography variant="caption">Not Written</Typography>
                  </Box>
                </Box>
              </Paper>
            </Panel>
          </ReactFlow>
        )}
      </Box>
    </Box>
  );
}

export default NarrativeGraphViewInner;
